import { supabase } from '@/integrations/supabase/client';

export interface ExperienceEntry {
  id?: string;
  role: string;
  dates: string;
  company: string;
  skills: string;
}

// Helper function to get current organization
const getCurrentOrganization = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data: userOrgs } = await supabase
    .from('user_organization')
    .select('organization_uuid')
    .eq('user_uuid', user.id)
    .single();

  if (!userOrgs) throw new Error('User not associated with any organization');
  return userOrgs.organization_uuid;
};

export const experienceStorage = {
  // Get all experiences for a candidate
  getExperiences: async (candidateId: string): Promise<ExperienceEntry[]> => {
    try {
      const { data, error } = await supabase
        .from('experiences')
        .select('*')
        .eq('candidate_id', candidateId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform database records to match UI interface
      return (data || []).map(exp => ({
        id: exp.id,
        role: exp.title,
        dates: exp.dates,
        company: exp.company,
        skills: Array.isArray(exp.skills) ? exp.skills.join(', ') : exp.skills || ''
      }));
    } catch (error) {
      console.error('Error reading experiences from database:', error);
      return [];
    }
  },

  // Add a new experience
  addExperience: async (candidateId: string, experience: Omit<ExperienceEntry, 'id'>): Promise<void> => {
    try {
      const orgId = await getCurrentOrganization();
      
      const { error } = await supabase
        .from('experiences')
        .insert({
          org_id: orgId,
          candidate_id: candidateId,
          title: experience.role,
          company: experience.company,
          dates: experience.dates,
          skills: experience.skills.split(',').map(s => s.trim()).filter(s => s)
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving experience to database:', error);
      throw new Error('Failed to save experience');
    }
  },

  // Remove an experience by ID
  removeExperience: async (experienceId: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('experiences')
        .delete()
        .eq('id', experienceId);

      if (error) throw error;
    } catch (error) {
      console.error('Error removing experience from database:', error);
      throw new Error('Failed to remove experience');
    }
  },

  // Update an experience by ID
  updateExperience: async (experienceId: string, experience: Omit<ExperienceEntry, 'id'>): Promise<void> => {
    try {
      const { error } = await supabase
        .from('experiences')
        .update({
          title: experience.role,
          company: experience.company,
          dates: experience.dates,
          skills: experience.skills.split(',').map(s => s.trim()).filter(s => s)
        })
        .eq('id', experienceId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating experience in database:', error);
      throw new Error('Failed to update experience');
    }
  },

  // Clear all experiences for a candidate
  clearExperiences: async (candidateId: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('experiences')
        .delete()
        .eq('candidate_id', candidateId);

      if (error) throw error;
    } catch (error) {
      console.error('Error clearing experiences from database:', error);
      throw new Error('Failed to clear experiences');
    }
  }
};
